import React, { useId, useMemo, useRef } from 'react';
import classNames from 'classnames';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { Icon } from '@/components';
import styles from './enhancedSelect.module.scss';

export interface ISelectOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

type Align = 'start' | 'end';
type Placement = 'top' | 'right' | 'bottom' | 'left';
export type AnchorProps = `${Placement}` | `${Placement} ${Align}`;

interface ISelectProps<T> {
  options: ISelectOption<T>[];
  value?: ISelectOption<T>;
  name?: string;
  className?: string;
  label?: string;
  menuClassName?: string;
  selectLabelClassName?: string;
  feedback?: string;
  isInvalid?: boolean;
  onChange: (_option: ISelectOption<T>) => void;
  placement?: AnchorProps;
  useFloatingLabel?: boolean;
}

const EnhancedSelect = <T,>({
  name = 'select',
  className,
  menuClassName,
  selectLabelClassName,
  options,
  value,
  label,
  isInvalid,
  feedback,
  onChange,
  placement = 'bottom start',
  useFloatingLabel,
}: ISelectProps<T>) => {
  const selectedOption = value;
  const selectedLabel = useMemo(() => selectedOption?.label, [selectedOption]);

  const buttonId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleOnChange = (option: ISelectOption<T>) => {
    onChange(option);
    setTimeout(() => {
      buttonRef.current?.blur();
    }, 0);
  };

  const getOptionKey = (optionValue: T, index: number): string | number => {
    if (typeof optionValue === 'string' || typeof optionValue === 'number') {
      return optionValue;
    }
    return `select-option-${name}-${index}`;
  };

  return (
    <Listbox
      by="value"
      name={name}
      onChange={handleOnChange}
      value={selectedOption}
    >
      {({ open }) => (
        <div className={classNames(className, styles.select)}>
          <ListboxButton
            className={classNames(styles.select__trigger, {
              [styles.select__invalid]: isInvalid,
            })}
            id={buttonId}
            ref={buttonRef}
          >
            <span
              className={classNames(
                styles.select__label,
                selectLabelClassName,
                {
                  [styles['hidde-label']]: label && !selectedLabel,
                  [styles['with-padding']]: useFloatingLabel,
                },
              )}
            >
              {selectedLabel}
            </span>
            {label && (
              <label
                className={classNames(styles.select__floating_label, {
                  [styles['is-floated']]: !!selectedOption && useFloatingLabel,
                  [styles['is-hidden']]: !!selectedOption && !useFloatingLabel,
                })}
                htmlFor={buttonId}
                onClick={e => e.preventDefault()}
              >
                {label}
              </label>
            )}
            <Icon
              className={classNames(styles.select__icon, {
                [styles.select__icon_open]: open,
              })}
              name={open ? 'angle-up' : 'angle-down'}
            />
          </ListboxButton>
          <ListboxOptions
            anchor={placement}
            aria-label={label || 'Select an option'}
            className={classNames(styles.select__menu, menuClassName)}
            modal={false}
          >
            <div className={styles.select__menu_body}>
              {options?.length > 0 &&
                options.map((option, index) => (
                  <ListboxOption
                    className={({ focus, selected }) =>
                      classNames(styles['select__menu-item'], {
                        [styles['is-active']]: focus,
                        [styles['is-selected']]: selected,
                      })
                    }
                    key={getOptionKey(option.value, index)}
                    value={option}
                  >
                    {option.label}
                  </ListboxOption>
                ))}
            </div>
          </ListboxOptions>

          {isInvalid && feedback && (
            <p className={styles.select__feedback}>
              <Icon name="info" /> {feedback}
            </p>
          )}
        </div>
      )}
    </Listbox>
  );
};

export { EnhancedSelect };
